import { useState, useEffect } from "react";
import { TOKEN_KEY, API_URL } from "./../../constants";
import { useApiUrl, useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { useForm } from "@refinedev/antd";
import { Form, Row, Col, Input, Card, Space, Button, Typography, Alert, Tooltip } from "antd";
import { SaveOutlined, SettingOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { PageHero, InitialAvatar } from "components";
import { useTour } from "components/tour/TourProvider";
import { useTranslation } from "react-i18next";
import { update } from "lodash";

const { Text } = Typography;

export const PreferencePage: React.FC = () => {
    const { t } = useTranslation();
    const { formProps, saveButtonProps, onFinish } = useForm();    
    const [errorMsg,setErrorMsg] = useState("");
    const { mutate } = useCustomMutation();
    const { startFullTour } = useTour();


    // Pega dados pessoais/preferências direto da custom endpoint
    const url = `${API_URL}/auth/me`;
    const { data } = useCustom<{data: any; user: any;}>({ url, method: "get" });
    const user : any = data?.data?.user;

    const handleSubmit = async() => {

        let values : any = formProps.form?.getFieldsValue();
        console.log(formProps.form?.getFieldsValue());

        let password = values.password;
        let newPassword = values.newPassword;
        let newPasswordConfirm = values.newPasswordConfirm;
        
        setErrorMsg("");
        const strongPassword = new RegExp("(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})");

        if(!password){
            setErrorMsg("Insira sua senha atual")
            formProps.form?.setFields([ { name: "password", errors: ["Insira sua senha atual"] }, ]);
            return false;
        }
        if(!newPassword || !newPasswordConfirm){
            setErrorMsg("Insira sua nova senha e a confirmação")
            formProps.form?.setFields([ { name: !newPassword ? "newPassword" : "newPasswordConfirm", errors: ["Insira sua nova senha e a confirmação"] }, ]);
            return false;
        }
        if(newPassword!==newPasswordConfirm){
            setErrorMsg("As novas senhas não são iguais")
            formProps.form?.setFields([ { name: "newPassword", errors: ["As novas senhas não são iguais"] }, ]);
            formProps.form?.setFields([ { name: "newPasswordConfirm", errors: ["As novas senhas não são iguais"] }, ]);
            return false;
        }
        if(newPassword==password){
            setErrorMsg("Sua nova senha é a mesma que a atual, crie uma nova senha diferente")
            formProps.form?.setFields([ { name: "password", errors: ["Sua nova senha é a mesma que a atual, crie uma nova senha diferente"] }, ]);
            formProps.form?.setFields([ { name: "newPasswordConfirm", errors: ["Sua nova senha é a mesma que a atual, crie uma nova senha diferente"] }, ]);
            return false;
        }
        if(!strongPassword.test(newPassword)){
            setErrorMsg("Sua nova senha deve conter no mínimo 8 caracteres e conter ao menos uma letra minúscula, uma letra maiucúscula e um número")
            formProps.form?.setFields([ { name: "newPassword", errors: ["Sua nova senha deve conter no mínimo 8 caracteres e conter ao menos uma letra minúscula, uma letra maiucúscula e um número"] }, ]);
            return false;
        }

        mutate({
            url: `${API_URL}/auth/password/edit`,
            method: "post",
            values: {
                password,
                newPassword,
                newPasswordConfirm
            },        
            successNotification: (data, values) => {
                formProps.form?.setFields([ { name: "password", value: null }, { name: "newPassword", value: null }, { name: "newPasswordConfirm", value: null } ]);                
                return {
                    message: `Senha alterada`,
                    description: "Sua nova senha foi cadastrada com sucesso",
                    type: "success",
                };
            },
            errorNotification: (data, values) => {
                return {
                    message: `Oops! Houve um erro`,
                    description: "Não conseguimos atualizar sua senha, tente novamente ou entre em contato com o nosso suporte.",
                    type: "error",
                };
            }
        });

    }

    // console.log(formProps);

    return (
        <>
            <PageHero
                eyebrow="Conta"
                title="Preferências"
                subtitle="Gerencie seus dados pessoais e mantenha sua conta segura."
                icon={<SettingOutlined />}
                tone="slate"
                actions={
                    <Button
                        type="primary"
                        ghost
                        size="large"
                        icon={<PlayCircleOutlined />}
                        onClick={() => startFullTour()}
                        data-tour="preferences-tour"
                    >
                        Iniciar tour guiado
                    </Button>
                }
            />
            <div className="premium-form">
                <Row gutter={[24, 24]} wrap>
                    <Col xl={10} lg={10} md={24} sm={24} xs={24} data-tour="preferences-profile">
                        <Card
                            bordered={false}
                            style={{ borderRadius: 16, border: "1px solid var(--border-color)", boxShadow: "var(--shadow-md)" }}
                            title={<span style={{ fontWeight: 700, fontSize: "1.05rem" }}>Seu perfil</span>}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 22 }}>
                                <InitialAvatar name={user?.name} size={64} />
                                <div style={{ minWidth: 0 }}>
                                    <Text style={{ display: "block", fontSize: 18, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
                                        {user?.name || "-"}
                                    </Text>
                                    <Text style={{ display: "block", fontSize: 13, color: "var(--text-tertiary)" }}>
                                        {user?.email || ""}
                                    </Text>
                                </div>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
                                <div>
                                    <Text style={{ display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-tertiary)", fontWeight: 600 }}>
                                        {t("users.fields.name")}
                                    </Text>
                                    <Text style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>{user?.name}</Text>
                                </div>
                                <div>
                                    <Text style={{ display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-tertiary)", fontWeight: 600 }}>
                                        {t("users.fields.email")}
                                    </Text>
                                    <Text style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>{user?.email}</Text>
                                </div>
                            </div>
                        </Card>
                    </Col>

                    <Col xl={14} lg={14} md={24} sm={24} xs={24}>
                        <Card
                            bordered={false}
                            style={{ borderRadius: 16, border: "1px solid var(--border-color)", boxShadow: "var(--shadow-md)" }}
                            title={<span style={{ fontWeight: 700, fontSize: "1.05rem" }}>Alterar senha</span>}
                        >
                        <Form
                            {...formProps}
                            // form={form}
                            layout="vertical"
                            initialValues={{
                                active: true,
                                // ...formProps
                            }}
                            onFinish={handleSubmit}
                        >       
                            <Form.Item
                                label={t("pages.updatePassword.fields.password")}
                                name="password"
                                rules={[
                                    {
                                        required: true,
                                    },
                                ]}
                                tooltip={"Sua senha atual"}
                            >
                                <Input.Password />
                            </Form.Item> 
                            <Form.Item
                                label={t("pages.updatePassword.fields.newPassword")}
                                name="newPassword"
                                rules={[
                                    {
                                        required: true,
                                    },
                                ]}
                                tooltip={"Sua nova senha deve conter no mínimo 8 caracteres e conter ao menos uma letra minúscula, uma letra maiucúscula e um número"}
                            >
                                    <Input.Password />
                            </Form.Item>       
                            <Form.Item
                                label={t("pages.updatePassword.fields.confirmNewPassword")}
                                name="newPasswordConfirm"
                                rules={[
                                    {
                                        required: true,
                                    },
                                ]}
                                tooltip={"Sua nova senha deve conter no mínimo 8 caracteres e conter ao menos uma letra minúscula, uma letra maiucúscula e um número"}
                            >
                                <Input.Password />
                            </Form.Item> 
                            {errorMsg && (
                                <Space direction="vertical" style={{ width: '100%', marginBottom: 15 }}>
                                    <Alert
                                        message="ATENÇÃO:"
                                        description={errorMsg}
                                        type="error"
                                        showIcon
                                    />
                                </Space>
                            )}       
                            <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
                                <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SaveOutlined />} 
                                size="large">
                                    Salvar
                                </Button>
                            </Form.Item>
                        </Form>
                        </Card>
                    </Col>
                    
            </Row>
            </div>
        </>
    );
};
